import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipRefProps } from 'react-tooltip';
import { useGraphGuide } from './GraphGuideContext';
import type { GuidePageId } from './guideContent';
import styles from '../SocialGraphPOC.module.scss';

// Anchors that have a matching guide page set this attribute next to their
// data-tooltip-* pair; the bubble then grows a "Learn more" control that opens
// the guide on that page.
export const GUIDE_PAGE_ATTR = 'data-guide-page';
export const GUIDE_SECTION_ATTR = 'data-guide-section';

type GuideHintTooltipProps = {
  id: string;
  place: 'top' | 'right' | 'bottom' | 'left';
};

// The graph's shared ⓘ hint bubble. Compared with a bare react-tooltip it
// lingers briefly after the cursor leaves the trigger and stays open while the
// cursor is over the bubble itself (`clickable`), so the "Learn more" control
// is reachable by mouse. Keyboard users reach the same pages through the
// header's "Graph guide" button — the bubble is not in the tab order.
export const GuideHintTooltip: React.FC<GuideHintTooltipProps> = ({
  id,
  place,
}) => {
  const { t } = useTranslation();
  const { openGuide } = useGraphGuide();
  const tooltipRef = useRef<TooltipRefProps>(null);

  return (
    <Tooltip
      ref={tooltipRef}
      id={id}
      className={styles.actorHintTooltip}
      place={place}
      positionStrategy="fixed"
      delayShow={350}
      delayHide={250}
      clickable
      // the tail is drawn by .actorHintTooltip::before (Figma's 4×11 triangle)
      noArrow
      render={({ content, activeAnchor }) => {
        const page = activeAnchor?.getAttribute(
          GUIDE_PAGE_ATTR,
        ) as GuidePageId | null;
        const section =
          activeAnchor?.getAttribute(GUIDE_SECTION_ATTR) ?? undefined;
        return (
          <>
            {content}
            {page && (
              <button
                type="button"
                className={styles.hintLearnMore}
                onClick={() => {
                  // the guide's overlay covers the trigger, so the bubble would
                  // otherwise sit open until the next mouse move
                  tooltipRef.current?.close();
                  openGuide(page, section);
                }}
              >
                {t('Learn more')}
                {/* arrow glyph is part of the design, kept out of the translatable string */}
                <span aria-hidden="true"> →</span>
              </button>
            )}
          </>
        );
      }}
    />
  );
};
